import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import { useCallback, useMemo, useState } from 'react';

import {
  type NotificationSubscriptionDataType,
  useNotificationMetaKPILabel,
  useNotificationSubscriptionsList,
} from '@/api/settings';
import Edit from '@/assets/svg/edit.svg?react';
import Delete from '@/assets/svg/trashcan.svg?react';
import {
  InstantSearch,
  InstantSearchDataTable,
  InstantSearchSortOrder,
  type InstantSearchDataTableColumnDef,
  type QueryState,
  type SolQueryParamsNew,
} from '@/components/InstantSearch';
import { getSolQueryParamsNewFromQueryState } from '@/components/InstantSearch/util/search-util';
import { MoreActionsCell } from '@/components/MoreActionsCell';
import { Panel } from '@/components/panels';
import { Tabs, TabsList, TabsTrigger } from '@/components/Tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip';
import { ColorBadge } from '@/components/typography';
import { USDateViewFormat } from '@/constants';
import { UserFilter } from '@/features/filters';
import {
  GoalNotificationSubscriptionDialog,
  RecurringNotificationSubscriptionDialog,
} from '@/features/NotificationSubscriptionModal';
import { getWeekdayNames } from '@/helper/dateHelper';
import { numberFormat } from '@/helper/numberFormatter';
import { useNotification } from '@/providers/Notification';
import { useMe } from '@/providers/User';
import { COLOR_AQUA_600, COLOR_GREEN_400, COLOR_PINK_400 } from '@/styles/palette';

import DeleteNotificationSubscriptionDialog from './DeleteNotificationSubscriptionDialog';

enum ActionKey {
  EDIT = 'EDIT',
  DELETE = 'DELETE',
}

const DEFAULT_QUERY_STATE: Partial<QueryState> = {
  sortBy: 'createdAt',
  sortOrder: InstantSearchSortOrder.DESC,
};

const NotificationSubscriptionsTable = () => {
  const currentUser = useMe();
  const { pushNotification } = useNotification();
  const [tableParams, setTableParams] = useState<SolQueryParamsNew>();
  const [subscriptionToDelete, setSubscriptionToDelete] =
    useState<NotificationSubscriptionDataType | null>(null);
  const [subscriptionToEdit, setSubscriptionToEdit] =
    useState<NotificationSubscriptionDataType | null>(null);
  const [lastSubscriptionKey, setLastSubscriptionKey] = useState<string | null>(null);
  const getKPILabel = useNotificationMetaKPILabel();
  const [subscriptionType, setSubscriptionType] = useState<'all' | 'achieved'>('all');

  const { subscriptions, totalResults, isLoading, error, mutate } =
    useNotificationSubscriptionsList(tableParams, subscriptionType);

  const handleQueryStateChange = useCallback((queryState: Required<QueryState>) => {
    const tableParams = getSolQueryParamsNewFromQueryState(queryState);
    setTableParams((currentTableParams) =>
      isEqual(currentTableParams, tableParams) ? currentTableParams : tableParams,
    );
  }, []);
  const handleSubscriptionTypeChange = (value: string) => {
    if (value === 'all' || value === 'achieved') {
      setSubscriptionType(value);
    }
  };

  const handleActionSelect = (key: ActionKey, field: NotificationSubscriptionDataType) => {
    switch (key) {
      case ActionKey.EDIT:
        setSubscriptionToEdit(field);
        setLastSubscriptionKey(field.id);
        break;
      case ActionKey.DELETE:
        setSubscriptionToDelete(field);
        break;
    }
  };

  const handleSubscriptionUpdate = (success: boolean) => {
    setSubscriptionToEdit(null);
    if (success) {
      pushNotification({ type: 'success', message: t`Notification updated successfully` });
      mutate();
    } else {
      pushNotification({
        type: 'error',
        message: t`Failed to update notification. Please try again or contact support if you encounter the issue again.`,
      });
    }
  };

  const handleSubscriptionDelete = (isSubscriptionOwner: boolean, success: boolean) => {
    if (success) {
      mutate();

      pushNotification({
        type: 'success',
        message: isSubscriptionOwner
          ? t`Successfully deleted notification`
          : t`Successfully unsubscribed from notification`,
      });
    } else {
      pushNotification({
        type: 'error',
        message: isSubscriptionOwner
          ? t`An error occurred while attempting to delete the notification. Please try again or contact support if you encounter the issue again.`
          : t`An error occurred while attempting to unsubscribe from the notification. Please try again or contact support if you encounter the issue again.`,
      });
    }

    setSubscriptionToDelete(null);
  };

  const columns = useMemo<InstantSearchDataTableColumnDef<NotificationSubscriptionDataType>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t`Notification Name`,
        minSize: 116,
        size: 350,
        flex: 3,
        enableSorting: true,
      },
      {
        id: 'createdBy',
        accessorFn: (field) => field.createdBy.name,
        header: t`Owner`,
        minSize: 88,
        maxSize: 150,
        flex: 1,
        enableSorting: true,
        cell: ({ row }) => (
          <Tooltip>
            <TooltipTrigger render={<span>{row.original.createdBy.name}</span>} />
            <TooltipContent>{row.original.createdBy.email}</TooltipContent>
          </Tooltip>
        ),
        filterComponent: (props) => <UserFilter {...props} />,
      },
      {
        id: 'delivery',
        header: t`Delivery`,
        enableSorting: false,
        minSize: 75,
        flex: 1,
        cell: ({ row }) => {
          const field = row.original;

          if (field.type === 'goal') {
            return `${numberFormat(field.goal, { compact: true })} ${getKPILabel(field.kpiId)}`;
          } else {
            if (
              field.monday &&
              field.tuesday &&
              field.wednesday &&
              field.thursday &&
              field.friday &&
              !field.saturday &&
              !field.sunday
            ) {
              return t`Every Weekday`;
            }

            const weekdaysForDelivery = [
              field.sunday,
              field.monday,
              field.tuesday,
              field.wednesday,
              field.thursday,
              field.friday,
              field.saturday,
            ];
            const weekdayNames = getWeekdayNames('short');
            const deliveryDays = weekdaysForDelivery.reduce<string[]>((memo, isActive, index) => {
              if (isActive) {
                memo.push(weekdayNames[index]);
              }
              return memo;
            }, []);

            if (deliveryDays.length === 7) {
              return t`Every Day`;
            }

            return deliveryDays.join(', ');
          }
        },
      },
      {
        accessorKey: 'createdAt',
        header: t`Creation Date`,
        minSize: 100,
        maxSize: 140,
        meta: { align: 'end' },
        enableSorting: true,
        cell: ({ getValue }) => dayjs(getValue<string>()).format(USDateViewFormat),
      },
      {
        accessorKey: 'type',
        header: t`Type`,
        size: 90,
        meta: { align: 'center' },
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          let label = t`Recurring`;
          let color = COLOR_AQUA_600;
          let textColor: 'light' | 'dark' | 'auto' = 'light';
          if (field.type === 'ai_digest') {
            label = t`AI Digest`;
            color = COLOR_PINK_400;
            textColor = 'light';
          } else if (field.type === 'goal') {
            label = t`Goal`;
            color = COLOR_GREEN_400;
            textColor = 'dark';
          }
          return (
            <ColorBadge color={color} textColor={textColor} showTooltip={false}>
              {label}
            </ColorBadge>
          );
        },
      },
      {
        id: 'repeatsWeeks',
        accessorFn: (field) => (field.type === 'goal' ? undefined : field.repeatsWeeks),
        header: t`Repeats`,
        minSize: 96,
        maxSize: 120,
        meta: { align: 'end' },
        enableSorting: true,
        cell: ({ row }) => {
          const field = row.original;

          if (field.type === 'goal') {
            return t`Never`;
          }

          if (field.repeatsWeeks === '1') {
            return t`Every Week`;
          } else {
            return t`Every ${numberFormat(field.repeatsWeeks)} Weeks`;
          }
        },
      },
      {
        id: 'actions',
        header: '',
        size: 75,
        meta: { align: 'end' },
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          const isSubscriptionOwner = field.createdBy.id === currentUser!.id;

          return (
            <MoreActionsCell<ActionKey>
              menuItems={[
                field.type === 'goal' &&
                  field.achieved !== true && {
                    key: ActionKey.EDIT,
                    label: t`Edit`,
                    icon: <Edit />,
                    isHidden: !isSubscriptionOwner,
                  },
                {
                  key: ActionKey.DELETE,
                  label: t`Delete`,
                  danger: true,
                  icon: <Delete />,
                },
              ]}
              onSelect={(key: ActionKey) => handleActionSelect(key, field)}
            />
          );
        },
      },
    ],
    [currentUser, getKPILabel],
  );

  return (
    <Panel
      title={t`Notification Subscriptions`}
      actions={
        <Tabs value={subscriptionType} onValueChange={handleSubscriptionTypeChange}>
          <TabsList>
            <TabsTrigger value="all">
              <Trans>All Notifications</Trans>
            </TabsTrigger>
            <TabsTrigger value="achieved">
              <Trans>Achieved Goals</Trans>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      }
    >
      <InstantSearch
        prefix="s"
        defaultQueryState={DEFAULT_QUERY_STATE}
        onQueryStateChange={handleQueryStateChange}
      >
        <InstantSearchDataTable<NotificationSubscriptionDataType>
          columns={columns}
          data={subscriptions}
          totalResults={totalResults}
          loading={isLoading}
          emptyMessage={
            subscriptionType === 'achieved' ? (
              <Trans>No achieved goals found.</Trans>
            ) : (
              <Trans>No notification subscriptions found.</Trans>
            )
          }
          error={error}
          getRowId={(record) => record.id}
          initialColumnPinning={{
            left: ['name'],
            right: ['actions'],
          }}
        />
        <DeleteNotificationSubscriptionDialog
          subscription={subscriptionToDelete}
          onCancel={() => setSubscriptionToDelete(null)}
          onDeleteComplete={handleSubscriptionDelete}
        />
        {subscriptionToEdit &&
          (subscriptionToEdit.type === 'goal' ? (
            <GoalNotificationSubscriptionDialog
              key={lastSubscriptionKey}
              subscription={subscriptionToEdit}
              onCancel={() => setSubscriptionToEdit(null)}
              onSaveComplete={handleSubscriptionUpdate}
            />
          ) : (
            <RecurringNotificationSubscriptionDialog
              key={lastSubscriptionKey}
              subscription={subscriptionToEdit}
              notificationType={subscriptionToEdit.type === 'ai_digest' ? 'ai_digest' : 'recurring'}
              onCancel={() => setSubscriptionToEdit(null)}
              onSaveComplete={handleSubscriptionUpdate}
            />
          ))}
      </InstantSearch>
    </Panel>
  );
};

export default NotificationSubscriptionsTable;
